import { useMemo, useState, type FormEvent } from 'react';
import {
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  Loader2,
  Sparkles,
  SkipForward,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { useComparateurStore } from './useComparateurStore';
import { StepEyebrow } from './StepEyebrow';
import {
  filterUsagesByEnergy,
  resolveSectorProfile,
  type SectorProfile,
  type SectorUsage,
} from '@shared/constants/naf-usages';
import { getUsageIcon } from './usage-icons';
import { cn } from '../../../lib/cn';

const ENERGY_FILTER_HINT: Record<'electricity' | 'gas' | 'both', string> = {
  electricity: 'Filtré sur vos usages électriques',
  gas: 'Filtré sur vos usages gaz',
  both: 'Tous vos usages électricité + gaz',
};

export function StepUsages() {
  const store = useComparateurStore();
  const dossier = store.dossier;
  const nafCode = dossier?.company?.nafCode ?? null;
  const energy = dossier?.energyChoice ?? 'both';

  const profile = useMemo<SectorProfile | null>(() => {
    return resolveSectorProfile(nafCode);
  }, [nafCode]);

  const usages = useMemo<SectorUsage[]>(() => {
    if (!profile) return [];
    return filterUsagesByEnergy(profile, energy);
  }, [profile, energy]);

  const defaultSelection = useMemo(() => {
    const existing = dossier?.usages?.usageIds;
    if (Array.isArray(existing) && existing.length > 0) {
      return new Set(existing);
    }
    return new Set(usages.filter((u) => u.recommended).map((u) => u.id));
  }, [dossier, usages]);

  const [selected, setSelected] = useState<Set<string>>(defaultSelection);
  const [notes, setNotes] = useState<string>(dossier?.usages?.notes ?? '');

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!profile) {
      store.skipUsages();
      return;
    }
    try {
      await store.saveUsages({
        sectorProfile: profile.id,
        usageIds: Array.from(selected),
        notes: notes.trim() ? notes.trim() : undefined,
      });
    } catch {
      /* error handled by store */
    }
  }

  if (!profile) {
    return (
      <div>
        <StepEyebrow step="usages" override="Étape optionnelle" />
        <h3 className="mt-2 h-section">
          On passe directement aux factures
        </h3>
        <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
          Votre code NAF correspond à un secteur standard — pas de profil sectoriel spécifique à
          configurer. Nos consultants adapteront le comparatif à votre situation lors de l&apos;échange.
        </p>
        <Button
          variant="brand"
          size="xl"
          className="mt-6 w-full gap-2"
          onClick={() => store.skipUsages()}
        >
          Continuer vers les factures
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  const totalCount = profile.usages.length;
  const ProfileIcon = getUsageIcon(profile.iconKey);

  return (
    <div>
      <button
        type="button"
        onClick={() => store.goBack()}
        className="-ml-1 mb-5 inline-flex min-h-11 items-center gap-1 rounded-md px-2 py-2 text-[12px] text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-700"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Retour
      </button>

      <StepEyebrow step="usages" />
      <h3 className="mt-2 h-section">
        Quels équipements consomment chez vous ?
      </h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
        Sélectionnez les postes que vous utilisez. Cela aide nos consultants à cibler les offres les
        plus pertinentes. <span className="font-semibold text-stone-700">Étape rapide et optionnelle.</span>
      </p>

      <div className="mt-5 flex items-center gap-3 rounded-xl border border-brand-100 bg-brand-50/60 px-4 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-[0_1px_3px_rgba(0,0,0,.05)]">
          <ProfileIcon className="h-4 w-4 text-brand-700" strokeWidth={1.7} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-brand-900">{profile.label}</p>
          <p className="mt-0.5 truncate text-[11.5px] text-brand-700">{profile.description}</p>
        </div>
        <Badge variant="default" size="sm" className="shrink-0">
          Profil détecté
        </Badge>
      </div>

      {energy !== 'both' && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-stone-500">
          <Sparkles className="h-3 w-3 text-accent-600" />
          {ENERGY_FILTER_HINT[energy]} ({usages.length}/{totalCount} usages affichés)
        </p>
      )}

      {store.error && (
        <Alert variant="error" className="mt-4">
          <AlertDescription className="text-[12px]">{store.error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={onSubmit} className="mt-6">
        <div className="grid gap-2.5 sm:grid-cols-2">
          {usages.map((usage) => {
            const isSelected = selected.has(usage.id);
            const Icon = getUsageIcon(usage.iconKey);
            return (
              <button
                key={usage.id}
                type="button"
                onClick={() => toggle(usage.id)}
                aria-pressed={isSelected}
                className={cn(
                  'group flex items-start gap-3 rounded-xl border-2 p-3.5 text-left transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                  isSelected
                    ? 'border-brand-400 bg-brand-50/70 shadow-[0_4px_12px_rgba(16,185,129,0.08)]'
                    : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50',
                )}
              >
                <span
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
                    isSelected
                      ? usage.energy === 'gas'
                        ? 'bg-lavender-100 text-lavender-700'
                        : 'bg-accent-100 text-accent-700'
                      : 'bg-stone-100 text-stone-500',
                  )}
                >
                  <Icon className="h-4 w-4" strokeWidth={1.7} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 text-[13px] font-semibold text-ink">
                    {usage.label}
                    {usage.energy !== 'both' && (
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em]',
                          usage.energy === 'gas'
                            ? 'bg-lavender-100 text-lavender-700'
                            : 'bg-accent-100 text-accent-700',
                        )}
                      >
                        {usage.energy === 'gas' ? 'Gaz' : 'Élec'}
                      </span>
                    )}
                  </p>
                  {usage.hint && (
                    <p className="mt-0.5 line-clamp-2 text-[11.5px] leading-snug text-stone-500">
                      {usage.hint}
                    </p>
                  )}
                </div>
                <span
                  className={cn(
                    'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors',
                    isSelected
                      ? 'border-brand-500 bg-brand-500 text-white'
                      : 'border-stone-300 bg-white',
                  )}
                >
                  {isSelected && <CheckCircle2 className="h-3 w-3" strokeWidth={3} />}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-5">
          <label
            htmlFor="usages-notes"
            className="text-[12px] font-semibold text-stone-700"
          >
            Un usage atypique ou une précision ?
            <span className="ml-1 font-normal text-stone-400">(optionnel)</span>
          </label>
          <textarea
            id="usages-notes"
            rows={2}
            value={notes}
            maxLength={1000}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ex&nbsp;: deuxième four, climatisation 24/7, process spécifique…"
            className="mt-1.5 w-full rounded-xl border-2 border-stone-200 bg-white p-3 text-[13px] text-ink placeholder:text-stone-400 focus:border-brand-400 focus:outline-none"
          />
        </div>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => store.skipUsages()}
            className="inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold text-stone-500 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
          >
            <SkipForward className="h-3.5 w-3.5" />
            Passer cette étape
          </button>
          <Button
            type="submit"
            variant="brand"
            size="xl"
            disabled={store.loading}
            className="gap-2 sm:w-auto"
          >
            {store.loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enregistrement…
              </>
            ) : (
              <>
                Continuer {selected.size > 0 && `(${selected.size})`}
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
